import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DocketFormDialog from "@/components/DocketFormDialog";
import DocketList from "@/components/DocketList";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { CalendarClock, Plus, Search, Scale, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "all", l: "All" }, { v: "open", l: "Open" }, { v: "pending", l: "Pending" }, { v: "completed", l: "Completed" },
];

export default function Docket() {
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({});
  const [sol, setSol] = useState({ alerts: [], count: 0 });
  const [tab, setTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [mine, setMine] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (tab !== "all") params.status = tab;
    if (typeFilter) params.entry_type = typeFilter;
    if (search.trim()) params.search = search.trim();
    if (mine) params.mine = true;
    api.get("/docket", { params }).then((r) => setEntries(r.data)).catch(() => {});
  }, [tab, typeFilter, search, mine]);

  useEffect(() => { api.get("/docket/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/docket/sol-alerts").then((r) => setSol(r.data)).catch(() => {}); }, [entries]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onStatus = async (e, status) => {
    try { await api.patch(`/docket/${e.id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/docket/${deleteTarget.id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="docket-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <CalendarClock className="w-7 h-7 text-[#002FA7]" /> Docket
          </h1>
          <p className="text-sm text-gray-500 mt-1">{entries.length} entr{entries.length === 1 ? "y" : "ies"}</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-docket-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New entry
        </button>
      </div>

      {sol.count > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-5" data-testid="docket-sol-banner">
          <p className="flex items-center gap-2 font-bold text-red-700 text-sm mb-2">
            <AlertTriangle className="w-4 h-4" /> {sol.count} statute-of-limitations deadline{sol.count === 1 ? "" : "s"} within 90 days
          </p>
          <div className="space-y-1">
            {sol.alerts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm" data-testid={`sol-alert-${a.id}`}>
                <span className="text-gray-700 truncate"><Scale className="w-3 h-3 inline text-red-500 mr-1" />{a.client_name} — {a.title}</span>
                <span className={`font-bold shrink-0 ml-3 ${a.days_left <= 30 ? "text-red-600" : "text-amber-600"}`}>{a.sol_date.slice(0, 10)} · {a.days_left}d</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-sm">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`docket-tab-${t.v}`}
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${tab === t.v ? "bg-white text-[#002FA7] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{t.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="docket-mine-toggle" className="accent-[#002FA7]" /> Assigned to me
          </label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="docket-type-filter"
            className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            <option value="">All types</option>
            {(meta.entry_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="docket-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
        </div>
      </div>

      <DocketList entries={entries} showClient onEdit={(e) => { setEditing(e); setDialogOpen(true); }}
        onDelete={setDeleteTarget} onStatus={onStatus} statuses={meta.statuses} />

      <DocketFormDialog open={dialogOpen} onOpenChange={setDialogOpen} entry={editing} meta={meta} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-docket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete docket entry?</AlertDialogTitle>
            <AlertDialogDescription>This entry{deleteTarget?.calendar_event_id ? " and its calendar event" : ""} will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-docket-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
